import './layout/appShellTheme.css';
import { mainNavigation, allBottomNavigation } from './layout/appNavigation';
import { appShellTranslator } from '../translations/appShell';
import { useLanguage } from '../contexts/LanguageContext';
import UserPlanBadge from './UserPlanBadge';
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  User,
  LogOut,
  Menu,
  X,
  Bell,
  Settings,
  CheckCircle2,
  XCircle,
  Clock,
  Search,
  Loader2,
  FileText,
  Layers,
  TestTube,
  ChevronRight,
  Building2
} from 'lucide-react';
import axios from 'axios';
import Logo from './Logo';
import DashboardFooter from './DashboardFooter';
import { BACKEND_URL } from '../utils/backendURL';
import { Badge } from './ui/badge';
import { useWorkspace } from '../contexts/WorkspaceContext';

const UserSidebarLayout = ({ 
  children, 
  user, 
  onLogout, 
  activeView, 
  onViewChange,
  notifications = [],
  unreadCount = 0,
  onNotificationClick,
  onMarkAllRead,
  onClearAll,
  onOpenProfile,
  planDisplayName  // Custom display name for plan badge
}) => {
  const { language } = useLanguage();
  const t = useMemo(() => appShellTranslator(language), [language]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [organization, setOrganization] = useState(null);
  
  // Workspace context for multi-tenant branding
  const { workspace, isMainApp, subdomain } = useWorkspace();
  
  // Global Search State
  const [globalSearchQuery, setGlobalSearchQuery] = useState('');
  const [globalSearchResults, setGlobalSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const searchInputRef = useRef(null);

  // Global Search function for Sprint QA
  const performGlobalSearch = useCallback(async (query) => {
    if (!query || query.trim().length < 1) {
      setGlobalSearchResults([]);
      setShowSearchResults(false);
      return;
    }
    
    try {
      setIsSearching(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/sprint-qa/search`, {
        params: { q: query.trim(), limit: 15 },
        headers: { Authorization: `Bearer ${token}` }
      });
      setGlobalSearchResults(response.data.results || []);
      setShowSearchResults(true);
    } catch (err) {
      console.error('Search failed:', err);
      setGlobalSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, []);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (globalSearchQuery.trim()) {
        performGlobalSearch(globalSearchQuery);
      } else {
        setGlobalSearchResults([]);
        setShowSearchResults(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [globalSearchQuery, performGlobalSearch]);

  // Keyboard shortcut for search (Ctrl+K)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
      if (e.key === 'Escape') {
        setShowSearchResults(false);
        searchInputRef.current?.blur();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Navigate to search result
  const navigateToSearchResult = useCallback((result) => {
    setShowSearchResults(false);
    setGlobalSearchQuery('');
    
    const projectSlug = result.project_slug;
    
    // If no project slug, we can't navigate properly - show error
    if (!projectSlug) {
      alert(t('Cannot navigate to {key}: Project information is missing. Please access it through the Sprint QA project view.', { key: result.key }));
      return;
    }
    
    if (result.type === 'requirement') {
      window.location.href = `/sprint-qa/project/${projectSlug}/requirement/${result.key}`;
    } else if (result.type === 'scenario') {
      window.location.href = `/sprint-qa/project/${projectSlug}/scenario/${result.key}`;
    } else if (result.type === 'test_case') {
      window.location.href = `/sprint-qa/project/${projectSlug}/test-case/${result.key}`;
    }
  }, [t]);

  // Show all items for admins/owners, filter for members
  const bottomNavigation = user?.role === 'owner' || user?.role === 'admin' 
    ? allBottomNavigation 
    : allBottomNavigation.filter(item => !item.adminOnly);

  const isActive = (view) => {
    // Direct match - activeView is the source of truth
    if (activeView === view) {
      return true;
    }
    
    // Special case: When user navigates to /sprint-qa URL directly (not via sidebar click),
    // and activeView hasn't been set yet, highlight Sprint QA based on URL
    if (view === 'sprint-qa' && window.location.pathname.includes('/sprint-qa')) {
      // Only highlight Sprint QA if no other view is currently active
      // This prevents Sprint QA from staying highlighted when user clicks Team/Billing
      const otherViews = ['dashboard', 'flow-testing', 'test-suites', 'projects', 'cicd', 'team', 'billing', 'plans', 'api-testing'];
      const isOtherViewActive = otherViews.includes(activeView);
      return !isOtherViewActive;
    }
    
    return false;
  };

  const handleNavigation = (view) => {
    // Special handling for sprint-qa - navigate to projects list URL
    if (view === 'sprint-qa') {
      window.history.pushState({}, '', '/sprint-qa');
    }
    
    if (onViewChange) {
      onViewChange(view);
    }
    setSidebarOpen(false);
  };

  const handleLogout = () => {
    onLogout();
  };

  // Close sidebar and notifications when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Close sidebar on mobile
      if (sidebarOpen && !event.target.closest('.sidebar-container') && !event.target.closest('.mobile-menu-button')) {
        setSidebarOpen(false);
      }
      
      // Close notifications dropdown
      if (showNotifications && !event.target.closest('.notifications-container')) {
        setShowNotifications(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [sidebarOpen, showNotifications]);

  // Listen for footer navigation events
  useEffect(() => {
    const handleNavigateToSupport = () => {
      if (onViewChange) {
        onViewChange('support');
      }
    };

    window.addEventListener('navigateToSupport', handleNavigateToSupport);
    return () => window.removeEventListener('navigateToSupport', handleNavigateToSupport);
  }, [onViewChange]);

  // Fetch organization data
  useEffect(() => {
    const fetchOrganization = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;

        const response = await axios.get(`${BACKEND_URL}/api/team/organization`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.data) {
          setOrganization(response.data);
        }
      } catch (error) {
        console.error('Failed to fetch organization:', error);
      }
    };

    fetchOrganization();
  }, []);

  return (
    <div className="lexa-app-shell fixed inset-0 flex overflow-hidden">
      {/* Sidebar */}
      <aside
        className={`lexa-app-sidebar sidebar-container fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-gray-200 transform transition-transform duration-300 ease-in-out lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } flex flex-col`}
      >
        {/* Logo & Close Button */}
        <div className="border-b border-slate-200 flex-shrink-0">
          <div className="flex items-center justify-between h-16 px-6">
            <div className="flex items-center">
              <Logo size="md" />
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              aria-label={t("Close menu")}
              className="lg:hidden p-1 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
          
          {/* Organization Name */}
          {organization?.name && (
            <div className="px-6 pb-3">
              <div className="flex items-center gap-2">
                <Building2 className="w-3.5 h-3.5 text-gray-400" />
                <p className="text-xs font-medium text-gray-500 truncate">
                  {organization.name}
                </p>
              </div>
              {/* Workspace subdomain indicator (only when on workspace) */}
              {!isMainApp && workspace && (
                <p className="text-[10px] text-purple-500 mt-0.5 ml-5">
                  {subdomain}.lexa.run
                </p>
              )}
            </div>
          )}
        </div>

        {/* Main Navigation - Scrollable */}
        <nav className="flex-1 overflow-y-auto py-6 px-3">
          <div className="space-y-1">
            {mainNavigation.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.view);
              
              return (
                <button
                  key={item.view}
                  onClick={() => handleNavigation(item.view)}
                  className={`w-full flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-all ${
                    active
                      ? 'bg-violet-50 text-violet-700'
                      : 'text-slate-600 hover:bg-slate-50 hover:text-slate-950'
                  }`}
                >
                  <Icon className={`w-5 h-5 mr-3 ${active ? 'text-violet-700' : 'text-slate-400'}`} />
                  <div className="flex-1 text-left">
                    <div className={active ? 'text-violet-700' : 'text-slate-950'}>
                      {t(item.name)}
                    </div>
                    {active && (
                      <div className="text-xs text-violet-600 mt-0.5">
                        {t(item.description)}
                      </div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>

          {/* Divider */}
          <div className="my-6 border-t border-gray-200"></div>

          {/* Bottom Navigation */}
          <div className="space-y-1">
            {bottomNavigation.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.view);
              
              return (
                <button
                  key={item.view}
                  onClick={() => handleNavigation(item.view)}
                  className={`w-full flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-all ${
                    active
                      ? 'bg-violet-50 text-violet-700'
                      : 'text-slate-600 hover:bg-slate-50 hover:text-slate-950'
                  }`}
                >
                  <Icon className={`w-5 h-5 mr-3 ${active ? 'text-violet-700' : 'text-slate-400'}`} />
                  <span>{t(item.name)}</span>
                </button>
              );
            })}
          </div>
        </nav>

        {/* User Profile Section - Fixed at Bottom */}
        <div className="flex-shrink-0 border-t border-gray-200">
          <div className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center flex-1 min-w-0">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 rounded-full bg-[#6d28d9] flex items-center justify-center">
                    <span className="text-white font-semibold text-sm">
                      {user?.name?.charAt(0)?.toUpperCase() || user?.email?.charAt(0)?.toUpperCase() || 'U'}
                    </span>
                  </div>
                </div>
                <div className="ml-3 flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {user?.name || t("User")}
                  </p>
                  <p className="text-xs text-gray-500 truncate">
                    {user?.email}
                  </p>
                </div>
              </div>
              
              {/* Logout Icon */}
              <button
                onClick={handleLogout}
                className="p-1.5 rounded-lg hover:bg-red-50 transition-colors group"
                title={t("Log out")}
              >
                <LogOut className="w-4 h-4 text-gray-400 group-hover:text-red-600" />
              </button>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 min-h-0 min-w-0 flex flex-col lg:ml-64 overflow-hidden">
        {/* Top Header Bar - Fixed with page background - Compact */}
        <header className="lexa-app-topbar min-h-12 flex flex-wrap items-center justify-between gap-y-2 py-2 sm:h-12 sm:flex-nowrap sm:py-0 px-4 lg:px-6 flex-shrink-0 sticky top-0 z-10">
          {/* Mobile Menu Button */}
          <button
            onClick={() => setSidebarOpen(true)}
            aria-label={t("Open menu")}
            className="mobile-menu-button lg:hidden p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <Menu className="w-5 h-5 text-gray-600" />
          </button>

          {/* Global Search Bar */}
          <div className="order-3 w-full sm:order-none sm:w-auto sm:flex-1 sm:max-w-md sm:mx-4 relative search-container">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                ref={searchInputRef}
                type="text"
                placeholder={t("Search Sprint QA... (Ctrl+K)")}
                value={globalSearchQuery}
                onChange={(e) => setGlobalSearchQuery(e.target.value)}
                onFocus={() => globalSearchResults.length > 0 && setShowSearchResults(true)}
                className="w-full pl-9 pr-8 py-1.5 bg-white border border-gray-200 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-violet-600 focus:border-transparent"
                data-testid="global-search-input"
              />
              {isSearching && (
                <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 animate-spin" />
              )}
              {globalSearchQuery && !isSearching && (
                <button
                  onClick={() => { setGlobalSearchQuery(''); setShowSearchResults(false); }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            
            {/* Search Results Dropdown */}
            {showSearchResults && globalSearchResults.length > 0 && (
              <div 
                className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-xl border border-gray-200 max-h-96 overflow-y-auto z-50"
                onMouseDown={(e) => e.preventDefault()}
              >
                {globalSearchResults.map((result, idx) => (
                  <button
                    key={`${result.type}-${result.id}-${idx}`}
                    onClick={() => navigateToSearchResult(result)}
                    className="w-full px-3 py-2.5 flex items-start gap-3 hover:bg-violet-50 transition-colors text-left border-b last:border-b-0"
                    data-testid={`search-result-${result.key}`}
                  >
                    {/* Type Icon */}
                    <div className={`flex-shrink-0 w-7 h-7 rounded flex items-center justify-center ${
                      result.type === 'requirement' ? 'bg-blue-100 text-blue-600' :
                      result.type === 'scenario' ? 'bg-purple-100 text-purple-600' :
                      'bg-green-100 text-green-600'
                    }`}>
                      {result.type === 'requirement' ? <FileText className="w-3.5 h-3.5" /> :
                       result.type === 'scenario' ? <Layers className="w-3.5 h-3.5" /> :
                       <TestTube className="w-3.5 h-3.5" />}
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs font-semibold text-indigo-600">{result.key}</span>
                        <Badge className={`text-[10px] px-1.5 py-0 ${
                          result.type === 'requirement' ? 'bg-blue-100 text-blue-700' :
                          result.type === 'scenario' ? 'bg-purple-100 text-purple-700' :
                          'bg-green-100 text-green-700'
                        }`}>
                          {result.type === 'requirement' ? 'REQ' :
                           result.type === 'scenario' ? 'TS' : 'TC'}
                        </Badge>
                      </div>
                      <p className="text-sm text-gray-900 truncate">{result.title}</p>
                      <p className="text-xs text-gray-500">{result.project_name}</p>
                    </div>
                    
                    {/* Arrow */}
                    <ChevronRight className="flex-shrink-0 w-4 h-4 text-gray-400 self-center" />
                  </button>
                ))}
              </div>
            )}
            
            {/* No Results */}
            {showSearchResults && globalSearchQuery && globalSearchResults.length === 0 && !isSearching && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-xl border border-gray-200 p-3 z-50">
                <p className="text-sm text-gray-500 text-center">{t('No results for "{query}"', { query: globalSearchQuery })}</p>
              </div>
            )}
          </div>
          
          {/* Click outside to close search */}
          {showSearchResults && (
            <div 
              className="fixed inset-0 z-40" 
              onClick={() => setShowSearchResults(false)}
            />
          )}

          {/* Right Side Actions */}
          <div className="flex flex-1 min-w-0 flex-wrap justify-end items-center gap-2 sm:flex-initial">
            {/* Role Badge */}
            {user?.role && (
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                user.role === 'owner' || user.role === 'admin' 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-blue-100 text-blue-800'
              }`}>
                <User className="w-3 h-3 mr-1" />
                {t(user.role === 'owner' || user.role === 'admin' ? 'Administrator' : 'Member')}
              </span>
            )}
            
            {/* Plan Badge */}
            <UserPlanBadge plan={user?.plan} displayName={planDisplayName} />

            {/* Notifications */}
            <div className="relative notifications-container">
              <button 
                onClick={() => setShowNotifications(!showNotifications)}
                aria-label={t("Notifications")}
                className="relative p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <Bell className="w-4 h-4 text-gray-600" />
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[10px] rounded-full h-3.5 w-3.5 flex items-center justify-center font-medium">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>

              {/* Notifications Dropdown */}
              {showNotifications && (
                <div className="absolute right-0 top-12 w-80 sm:w-96 max-w-[calc(100vw-2rem)] bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                  <div className="p-4 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold text-gray-900">{t("Notifications")}</h3>
                      <div className="flex items-center space-x-2">
                        {unreadCount > 0 && (
                          <button
                            onClick={() => {
                              onMarkAllRead && onMarkAllRead();
                            }}
                            className="text-xs text-blue-600 hover:text-blue-800"
                          >
                            {t("Mark all read")}
                          </button>
                        )}
                        <button
                          onClick={() => {
                            onClearAll && onClearAll();
                          }}
                          className="text-xs text-gray-500 hover:text-gray-700"
                        >
                          {t("Clear all")}
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="max-h-96 overflow-y-auto">
                    {notifications.length === 0 ? (
                      <div className="p-8 text-center text-gray-500">
                        <Bell className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                        <p>{t("No notifications yet")}</p>
                        <p className="text-sm mt-1">{t("Scheduled test results will appear here")}</p>
                      </div>
                    ) : (
                      <div className="divide-y divide-gray-100">
                        {notifications.map((notification) => (
                          <div
                            key={notification.id}
                            className={`p-4 hover:bg-gray-50 cursor-pointer transition-colors ${
                              !notification.read ? 'bg-blue-50' : ''
                            }`}
                            onClick={() => {
                              onNotificationClick && onNotificationClick(notification);
                              setShowNotifications(false);
                            }}
                          >
                            <div className="flex items-start space-x-3">
                              <div className="flex-shrink-0">
                                {notification.type === 'success' ? (
                                  <CheckCircle2 className="h-5 w-5 text-green-500" />
                                ) : notification.type === 'error' ? (
                                  <XCircle className="h-5 w-5 text-red-500" />
                                ) : (
                                  <Clock className="h-5 w-5 text-blue-500" />
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className={`text-sm ${!notification.read ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
                                  {notification.title}
                                </p>
                                {notification.message && (
                                  <p className="text-sm text-gray-600 mt-1">{notification.message}</p>
                                )}
                                <p className="text-xs text-gray-500 mt-2">
                                  {new Date(notification.created_at || notification.timestamp).toLocaleString(language === 'es' ? 'es-ES' : 'en-US')}
                                </p>
                              </div>
                              {!notification.read && (
                                <div className="flex-shrink-0">
                                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Settings/Profile Button */}
            <button
              onClick={onOpenProfile}
              className="p-1.5 rounded-lg hover:bg-purple-50 transition-colors"
              title={t("Profile Settings")}
            >
              <Settings className="w-4 h-4 text-purple-600" />
            </button>

            {/* Mobile User Avatar */}
            <div className="lg:hidden">
              <div className="w-7 h-7 rounded-full bg-[#6d28d9] flex items-center justify-center">
                <span className="text-white font-semibold text-xs">
                  {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                </span>
              </div>
            </div>
          </div>
        </header>

        {/* Main content scrolls independently of the viewport-anchored footer. */}
        <main className="lexa-app-main flex-1 min-h-0 overflow-y-auto pb-12">
          <div className="py-6">
            <div className="mx-auto px-4 sm:px-6 lg:px-8">
              {children}
            </div>
          </div>
        </main>

        {/* Fixed Footer */}
        <div className="flex-shrink-0">
          <DashboardFooter />
        </div>
      </div>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default UserSidebarLayout;
